import React, { useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  Fab,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import { Res } from '../../resources';
import { AvatarComponent } from '../Avatar';
import { LogoComponent } from '../Logo';
import { useUser } from '../../core/hooks';

export const HeaderComponent: React.FC = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
  const { hasPermission, permissions } = useUser();
  const { feature_flags } = Res;
  const adminDebugLoggedRef = useRef(false);

  useEffect(() => {
    if (adminDebugLoggedRef.current) return;
    if (!permissions) return;

    // Temporary: help debug admin panel visibility in prod/dev
    // eslint-disable-next-line no-console
    console.log('admin flag', Res.feature_flags.ENABLE_ADMIN_PANEL);
    // eslint-disable-next-line no-console
    console.log('permissions', permissions);

    adminDebugLoggedRef.current = true;
  }, [permissions]);

  const handleNavMenuToggle = () => {
    setIsNavMenuOpen((currentIsNavMenuOpen) => !currentIsNavMenuOpen);
  };

  const isSurveyRequestPage = pathname === '/survey-request';

  const headerLinkStyle = {
    color: 'var(--eclipse-black)',
    outline: '1px solid var(--gentian-white)',
    outlineOffset: '5px',
    borderRadius: '3px',
    transition: 'outline-color 0.2s ease-in-out',
    ':focus, :hover': {
      outlineColor: 'var(--eclipse-black)',
    },
  };

  const drawerLinkStyle = {
    transition: 'background-color 0.2s ease-in-out',
    ':focus, :hover': {
      color: 'var(--gentian-white)',
      backgroundColor: 'var(--gentian-white)',
    },
  };

  const navigationItems = [
    {
      label: 'My Projects',
      path: '/dashboard',
      permissions: [],
      isEnabled: feature_flags.ENABLE_DASHBOARD,
    },
    {
      label: 'My Projects',
      path: '/projects',
      permissions: ['create:projects'],
      isEnabled: feature_flags.ENABLE_PROJECTS && hasPermission('create:projects'),
    },
    {
      label: 'Configure Survey Result',
      path: '/result-upload',
      permissions: ['create:results'],
      isEnabled: feature_flags.ENABLE_RESULT_UPLOAD && hasPermission('create:results'),
      style: {
        minWidth: '102px',
      },
    },
    {
      label: 'Manage Products',
      path: '/products',
      permissions: ['create:products'],
      isEnabled: feature_flags.ENABLE_PRODUCT_MANAGEMENT && hasPermission('create:products'),
    },
    {
      label: 'Admin Panel',
      path: '/reseller/admin-panel',
      permissions: [],
      isEnabled: feature_flags.ENABLE_ADMIN_PANEL && hasPermission('ADMIN:usage'),
    },
    {
      label: 'FAQs',
      path: '/faq',
      permissions: [],
      isEnabled: feature_flags.ENABLE_FAQ,
    },
  ].filter((item) => item.isEnabled);

  return (
    <AppBar>
      <Toolbar sx={{ gap: '32px', justifyContent: { xs: 'space-between', sm: 'inherit' } }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleNavMenuToggle}
          sx={{ display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        <Drawer
          anchor="left"
          open={isNavMenuOpen}
          onClose={handleNavMenuToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          disableScrollLock
          sx={{ display: { sm: 'none' }, width: '210px' }}
        >
          <Box onClick={handleNavMenuToggle} sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ my: 2 }}>
              Menu
            </Typography>
            <List>
              {navigationItems.map((item) => (
                <ListItem key={item.path} component={RouterLink} to={item.path} sx={drawerLinkStyle}>
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
        <Link component={RouterLink} to="/" color={theme.palette.common.white} height="45px">
          <LogoComponent />
        </Link>
        <Box alignItems="center" gap="32px" sx={{ display: { xs: 'none', sm: 'flex' } }} flex={1}>
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              component={RouterLink}
              to={item.path}
              sx={{ ...headerLinkStyle, ...item.style }}
            >
              <Typography>{item.label}</Typography>
            </Link>
          ))}
        </Box>
        <Box display="flex" alignItems="center" gap="16px">
          {!isSurveyRequestPage && (
            <>
              <Fab
                color="primary"
                aria-label="add"
                sx={{ position: 'fixed', bottom: 16, right: 16, display: { sm: 'none' } }}
                onClick={() => (window.location.href = '/survey-request')}
              >
                <AddIcon />
              </Fab>
              <Button
                variant="contained"
                component={RouterLink}
                to="/survey-request"
                sx={{
                  flex: 1,
                  minWidth: '105px',
                  textAlign: 'center',
                  display: { xs: 'none', sm: 'flex' },
                  backgroundColor: 'var(--gentian-blue)', // Set the background color to --sundrop-yellow
                  color: 'var(--gentian-white)', // Set the text color to --gentian-black for contrast
                  '&:hover': {
                    backgroundColor: 'var(--eclipse-black)',
                  },
                }}
              >
                Request a Survey
              </Button>
            </>
          )}
          <AvatarComponent />
        </Box>
      </Toolbar>
    </AppBar>
  );
};
